// frontend/src/pages/citizen/VisitSummary.js
// Curated, noise-free Visit Summary with resilient header derivation.
// Now includes a robust Medicines table (name, dose, frequency, duration, route, notes).
// No generic dumps; mobile-friendly; tolerates new/legacy endpoints.
// ✅ Tenant-safe navigation added (no regression).

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Divider, Grid, Chip, Button, CircularProgress, Alert, Stack,
  Table, TableHead, TableBody, TableRow, TableCell
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import http from '../../services/http';

// ---- tenant-safe navigation helpers ----
function getTenantBase() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/[^/]+/);
    return m ? m[0] : '';
  } catch {
    return '';
  }
}
function tenantUrl(path) {
  const base = getTenantBase();
  const p = String(path || '');
  const clean = p.startsWith('/') ? p : `/${p}`;
  return `${base}${clean}`.replace(/\/{2,}/g, '/');
}

// ✅ memoized query params (prevents unnecessary effect re-runs)
function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const t = (v) => (v == null || v === '' ? '—' : String(v));
const when = (dt) => { try { const d = new Date(dt); return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(); } catch { return '—'; } };
const first = (...vals) => vals.find(v => {
  if (v == null) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return String(v).trim() !== '';
});

async function tryGet(urls) {
  for (const url of urls) {
    try {
      const r = await http.get(url);
      if (r && r.status >= 200 && r.status < 300) return r.data;
    } catch { /* ignore */ }
  }
  return null;
}

const unwrap = (d, key) => {
  if (!d) return {};
  if (d[key]) return d[key];
  if (d.data && d.data[key]) return d.data[key];
  if (d.data && (typeof d.data === 'object') && !Array.isArray(d.data)) return d.data;
  return d;
};

const Section = ({ title, children }) => (
  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
    <Typography variant="h5" sx={{ mb: 1 }}>{title}</Typography>
    <Divider sx={{ mb: 1.5 }} />
    {children}
  </Paper>
);

const Row = ({ label, value }) => (
  <Grid container spacing={1} sx={{ mb: 0.5 }}>
    <Grid item xs={12} md={4}>
      <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
    </Grid>
    <Grid item xs={12} md={8}>
      <Typography variant="body1" component="div">{value || '—'}</Typography>
    </Grid>
  </Grid>
);

export default function VisitSummary() {
  const query = useQuery();
  const params = useParams();
  const navigate = useNavigate();

  const visitId =
    query.get('visitId') ||
    params.visitId ||
    localStorage.getItem('visitId') ||
    '';

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [meta, setMeta] = useState({});
  const [history, setHistory] = useState({});
  const [exam, setExam] = useState({});
  const [inv, setInv] = useState({});
  const [rx, setRx] = useState({});

  useEffect(() => {
    let alive = true;
    if (!visitId) { setLoading(false); setErr('No visit selected'); return; }

    (async () => {
      setLoading(true); setErr('');
      const id = encodeURIComponent(visitId);

      const m = await tryGet([`/api/visits/${id}`, `/api/visit/${id}`, `/api/visits/${id}/header`]);
      const h = await tryGet([`/api/visits/${id}/history`, `/api/visit-history/${id}`]);
      const e = await tryGet([`/api/visits/${id}/exam`, `/api/visit-exam/${id}`, `/api/visits/${id}/examination`]);
      const i = await tryGet([`/api/visits/${id}/investigations`, `/api/visit-investigations/${id}`]);
      const p = await tryGet([`/api/visits/${id}/prescription`, `/api/visit-prescription/${id}`]);

      if (!alive) return;

      setMeta(m || {});
      setHistory(unwrap(h, 'history'));
      setExam(unwrap(e, 'exam'));
      setInv(unwrap(i, 'investigations'));
      setRx(unwrap(p, 'prescription'));

      setLoading(false);
    })().catch((e) => {
      if (alive) {
        setErr(e?.message || 'Failed to load');
        setLoading(false);
      }
    });

    return () => { alive = false; };
  }, [visitId]);

  // ----- Encounter Header -----
  const header = useMemo(() => {
    const centre = first(meta.centre, meta.chc, history.centre, exam.centre, inv.centre, rx.centre, '') || '-';
    const status = first(meta.prescription?.status, meta.status, rx.status, '') || '-';
    const createdAt = first(meta.createdAt, meta.visitDate, history.createdAt, exam.createdAt, inv.createdAt, rx.createdAt, '');
    const createdByName = first(meta.createdByName, meta.createdBy, history.createdByName, exam.createdByName, inv.createdByName, rx.createdByName, '');
    const createdByRole = first(meta.createdByRole, history.createdByRole, exam.createdByRole, inv.createdByRole, rx.createdByRole, '');
    const approvedAt = first(meta.approvedAt, meta.prescription?.approvedAt, rx.approvedAt, '');
    const approvedByObj = first(meta.approvedBy, meta.prescription?.approvedBy, rx.approvedBy, null);
    const approvedByName = first(meta.approvedByName, meta.prescription?.approvedByName, rx.approvedByName, (approvedByObj && (approvedByObj.name || approvedByObj.by)), '');
    const approvedByRole = first(meta.approvedByRole, meta.prescription?.approvedByRole, rx.approvedByRole, (approvedByObj && approvedByObj.role), '');
    const returnNote = first(meta.returnNote, meta.prescription?.returnNote, rx.returnNote, '');
    return { centre, status, createdAt, createdByName, createdByRole, approvedAt, approvedByName, approvedByRole, returnNote };
  }, [meta, history, exam, inv, rx]);

  const statusChip = (s) => {
    const v = String(s || '').toLowerCase();
    let color = 'default';
    if (v === 'approved') color = 'success';
    else if (['submitted', 'pending', 'awaiting review'].includes(v)) color = 'warning';
    else if (v === 'returned') color = 'error';
    return <Chip size="small" color={color} label={s || '-'} />;
  };

  // ----- History -----
  const chiefComplaints = first(history.chiefComplaints, history.chiefComplaint, history.cc, history.hopi, '');
  const pastIllnesses = Array.isArray(history.pastIllnesses) ? history.pastIllnesses : [];
  const allergies = Array.isArray(history.allergies) ? history.allergies.join(', ') : t(history.allergies);
  const familyHistory = t(history.familyHistory);
  const personalHistory = t(history.personalHistory);
  const immunizationHistory = t(history.immunizationHistory);
  const travelExposures = t(history.recentTravel);
  const occupationalExposures = t(history.occupationalExposures);
  const environmentalExposures = t(history.environmentalExposures);
  const currentMeds = t(history.currentMedications);
  const adherence = t(history.medicationAdherence);
  const adr = t(history.adverseDrugReactions);

  // ----- Exam -----
  const vit = exam.vitals || {};
  const vitalsLine = [
    vit.pulse != null && `Pulse: ${vit.pulse}`,
    (vit.bpSystolic != null && vit.bpDiastolic != null) && `BP: ${vit.bpSystolic}/${vit.bpDiastolic}`,
    vit.respRate != null && `RR: ${vit.respRate}`,
    vit.tempC != null && `Temp: ${vit.tempC}°C`,
    vit.spo2 != null && `SpO₂: ${vit.spo2}%`,
    vit.bmi != null && `BMI: ${vit.bmi}`,
  ].filter(Boolean).join(' • ') || '—';

  const ge = exam.generalExam || {};
  const generalLine = [
    ge.pallor && `Pallor: ${ge.pallor}`,
    ge.icterus && `Icterus: ${ge.icterus}`,
    ge.cyanosis && `Cyanosis: ${ge.cyanosis}`,
    ge.clubbing && `Clubbing: ${ge.clubbing}`,
    ge.lymphadenopathy && `Lymphadenopathy: ${ge.lymphadenopathy}`,
    ge.edema && `Edema: ${ge.edema}`,
    ge.hydration && `Hydration: ${ge.hydration}`,
    ge.nutrition && `Nutrition: ${ge.nutrition}`,
    ge.consciousness && `Consciousness: ${ge.consciousness}`,
    ge.remarks && ge.remarks,
  ].filter(Boolean).join(' • ') || '—';

  const se = exam.systemicExam || {};
  const systemicLine = [
    se.cvs && `CVS: ${se.cvs}`,
    se.rs && `RS: ${se.rs}`,
    se.abdomen && `Abdomen: ${se.abdomen}`,
    se.cns && `CNS: ${se.cns}`,
    se.locomotor && `Locomotor: ${se.locomotor}`,
    se.other && se.other,
  ].filter(Boolean).join(' • ') || '—';

  // ----- Investigations -----
  const invOrdered = Array.isArray(inv.list) ? inv.list.map(x => x?.name || x).filter(Boolean).join(', ') : '';
  const hemoglobin = t(inv.hemoglobin);
  const tlc = t(inv.tlc);
  const dc = t(inv.dc);
  const sugarF = t(inv.bloodSugarFasting);
  const sugarP = t(inv.bloodSugarPost);
  const chestXray = t(inv.chestXray);
  const otherInv = t(inv.otherInvestigations);

  // ----- Prescription -----
  const ddx = t(first(rx.differentialDx, rx.provisionalDx, rx.provisionalDiagnosis, rx.diagnosis));
  const icdCodes = (() => {
    const raw = first(rx.icdCodes, rx.icd, rx.diagnoses, rx.codes, []);
    const arr = Array.isArray(raw) ? raw : [raw];
    const list = arr.map(x => x?.title || x?.name || x?.code || (typeof x === 'string' ? x : '')).filter(Boolean);
    return list.length ? list.join(', ') : '—';
  })();
  const advice = t(first(rx.advice, rx.counseling, rx.counselling, rx.instructions));
  const notes = t(first(rx.notes, rx.remarks, rx.comment));
  const returnNote = t(rx.returnNote);

  // Robust extraction for medicines (supports string medicines too)
  const medicinesArr = useMemo(() => {
    const raw = first(rx.medicines, rx.drugs, rx.items, rx.meds, []);
    const arr = Array.isArray(raw) ? raw : (raw ? [raw] : []);

    return arr.map((m) => {
      if (typeof m === 'string') {
        const name = t(m);
        return { name, dose: '—', freq: '—', duration: '—', route: '—', instr: '—' };
      }

      const name = first(m?.name, m?.title, m?.drugName, m?.drug?.name, m?.drug, '');
      const dose = first(m?.dose, m?.dosage, m?.strength, m?.doseStrength, '');
      const freq = first(m?.frequency, m?.freq, m?.sig, m?.schedule, m?.times, '');
      const duration = first(m?.duration, m?.days, m?.forDays, '');
      const route = first(m?.route, m?.administrationRoute, m?.roa, '');
      const instr = first(m?.instructions, m?.advice, m?.notes, m?.remark, m?.remarks, '');

      return {
        name: t(name),
        dose: t(dose),
        freq: t(freq),
        duration: t(duration),
        route: t(route),
        instr: t(instr),
      };
    }).filter(row =>
      row.name !== '—' ||
      row.dose !== '—' ||
      row.freq !== '—' ||
      row.duration !== '—' ||
      row.route !== '—' ||
      row.instr !== '—'
    );
  }, [rx]);

  // ✅ Tenant-safe Back
  const backTo = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(tenantUrl('/citizen-management'));
  };

  const downloadPdf = async () => {
    const urls = [
      `/api/visits/${encodeURIComponent(visitId)}/pdf`,
      `/api/visit-pdf/${encodeURIComponent(visitId)}`,
      `/api/visits/${encodeURIComponent(visitId)}/prescription/pdf`,
    ];
    for (const u of urls) {
      try {
        await http.download(u, `Visit_${visitId}.pdf`);
        return;
      } catch { /* ignore */ }
    }
    alert('PDF not available.');
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h4">Visit Summary</Typography>
        <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, md: 0 } }}>
          <Button variant="outlined" onClick={backTo}>Back</Button>
          <Button variant="contained" onClick={downloadPdf}>Download PDF</Button>
        </Stack>
      </Stack>

      {!visitId && <Alert severity="warning" sx={{ mb: 2 }}>No visit selected.</Alert>}
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {loading ? (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Paper sx={{ p: 2 }}><CircularProgress /></Paper>
          <Paper sx={{ p: 2 }}><CircularProgress /></Paper>
          <Paper sx={{ p: 2 }}><CircularProgress /></Paper>
          <Paper sx={{ p: 2 }}><CircularProgress /></Paper>
        </Box>
      ) : (
        <>
          {/* Encounter Header */}
          <Section title="Encounter Header">
            <Grid container spacing={1}>
              <Grid item xs={12} md={8}>
                <Row label="Centre" value={t(header.centre)} />
                <Row label="Created by" value={[header.createdByName, header.createdByRole && `(${header.createdByRole})`].filter(Boolean).join(' ') || '—'} />
                <Row label="Created at" value={when(header.createdAt)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Row label="Status" value={statusChip(header.status)} />
                <Row label="Approved by" value={[header.approvedByName, header.approvedByRole && `(${header.approvedByRole})`].filter(Boolean).join(' ') || '—'} />
                <Row label="Approved at" value={when(header.approvedAt)} />
              </Grid>
            </Grid>
            {header.returnNote && (
              <Box sx={{ mt: 1.5 }}>
                <Chip size="small" label="Return Note" sx={{ mr: 1 }} />
                <Typography variant="body2" sx={{ mt: 0.5 }}>{header.returnNote}</Typography>
              </Box>
            )}
          </Section>

          {/* History */}
          <Section title="Medical History">
            <Row label="Chief complaints / HOPI" value={t(chiefComplaints)} />
            <Row label="Past illnesses" value={pastIllnesses.length ? pastIllnesses.join(', ') : '—'} />
            <Row label="Allergies" value={allergies} />
            <Row label="Family history" value={familyHistory} />
            <Row label="Personal history" value={personalHistory} />
            <Row label="Immunization" value={immunizationHistory} />
            <Row label="Travel (recent)" value={travelExposures} />
            <Row label="Occupational exposures" value={occupationalExposures} />
            <Row label="Environmental exposures" value={environmentalExposures} />
            <Row label="Current medications" value={currentMeds} />
            <Row label="Adherence" value={adherence} />
            <Row label="Adverse drug reactions" value={adr} />
          </Section>

          {/* Exam */}
          <Section title="Clinical Examination">
            <Row label="Vitals" value={t(vitalsLine)} />
            <Row label="General exam" value={t(generalLine)} />
            <Row label="Systemic exam" value={t(systemicLine)} />
          </Section>

          {/* Investigations */}
          <Section title="Investigations">
            <Row label="Ordered list" value={invOrdered ? invOrdered : '—'} />
            <Row label="Hemoglobin" value={hemoglobin} />
            <Row label="TLC" value={t(tlc)} />
            <Row label="Differential count" value={t(dc)} />
            <Row label="Blood sugar (F)" value={sugarF} />
            <Row label="Blood sugar (PP)" value={sugarP} />
            <Row label="Chest X-ray" value={chestXray} />
            <Row label="Other" value={otherInv} />
          </Section>

          {/* Prescription */}
          <Section title="Prescription">
            <Row label="Differential Dx" value={ddx} />
            <Row label="ICD Codes" value={icdCodes} />
            <Row label="Advice" value={advice} />
            <Row label="Notes" value={notes} />

            {/* Medicines table */}
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Medicines</Typography>
              {medicinesArr.length === 0 ? (
                <Typography variant="body1" component="div">—</Typography>
              ) : (
                <Table size="small" sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '28%' }}>Name</TableCell>
                      <TableCell sx={{ width: '14%' }}>Dose</TableCell>
                      <TableCell sx={{ width: '16%' }}>Frequency</TableCell>
                      <TableCell sx={{ width: '12%' }}>Duration</TableCell>
                      <TableCell sx={{ width: '12%' }}>Route</TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {medicinesArr.map((m, i) => (
                      <TableRow key={i}>
                        <TableCell>{m.name}</TableCell>
                        <TableCell>{m.dose}</TableCell>
                        <TableCell>{m.freq}</TableCell>
                        <TableCell>{m.duration}</TableCell>
                        <TableCell>{m.route}</TableCell>
                        <TableCell>{m.instr}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>

            {returnNote !== '—' && <Row label="Return Note" value={returnNote} />}
          </Section>
        </>
      )}
    </Box>
  );
}
